// src/utils/fnct.ts — SCE Issues
// Règles des FNC-T et de leurs actions (chantier FNC-T, lot F1). Pur, testable.
//
// Les règles de calcul reprennent À L'IDENTIQUE celles de la base Excel
// « FNC-T & Actions » (statut d'une action, RPN, criticité), pour que les
// chiffres de l'appli et ceux du classeur se recoupent le jour de la bascule.
// Seuls les défauts du classeur sont corrigés : N° qui dépendait de la place de
// la ligne, noms tapés de trois façons, machines dans une cellule.

import type { ActionFnct, CriticiteRpn, Fnct, Jour, StatutAction } from '@/types/fnct.types'

// ─── N° ───

/** `FNC-T_2026-58`. Le rang n'a pas de zéro de tête (comme la base et RefFNCT). */
export function formatNumero(annee: number, rang: number): string {
  return `FNC-T_${annee}-${rang}`
}

/** `FNC-T_2026-58` → { annee: 2026, rang: 58 } ; null pour l'ancien format (`FNC-T 23-036`) ou un texte quelconque. */
export function lireNumero(texte: string | null | undefined): { annee: number; rang: number } | null {
  const m = /^\s*FNC-T_(\d{4})-(\d{1,4})\s*$/i.exec(texte ?? '')
  if (!m) return null
  return { annee: Number(m[1]), rang: Number(m[2]) }
}

/** Ordre naturel : par année, puis par rang (2026-9 avant 2026-10). */
export function comparerNumeros(a: string, b: string): number {
  const x = lireNumero(a)
  const y = lireNumero(b)
  if (!x || !y) return a.localeCompare(b)
  return x.annee - y.annee || x.rang - y.rang
}

/** `FNC-T_2026-58-A01` : l'action garde son rang dans la FNC-T. */
export function idAction(numero: string, rang: number): string {
  return `${numero}-A${String(rang).padStart(2, '0')}`
}

// ─── Statut d'une action ───

/**
 * Même règle que « Action status » dans la base Excel :
 *  - pas d'échéance → à planifier ;
 *  - close : à temps si la clôture tient l'échéance (la reportée si elle existe) ;
 *  - ouverte : en cours tant que l'échéance initiale n'est pas passée, reportée
 *    si une nouvelle échéance la couvre encore, en retard sinon.
 * Les dates `AAAA-MM-JJ` se comparent comme des textes.
 */
export function statutAction(a: Pick<ActionFnct, 'echeance' | 'echeanceReportee' | 'cloture'>, aujourdhui: Jour): StatutAction {
  if (!a.echeance) return 'a-planifier'
  if (a.cloture) {
    const limite = a.echeanceReportee ?? a.echeance
    return a.cloture <= limite ? 'terminee-a-temps' : 'terminee-en-retard'
  }
  if (a.echeance >= aujourdhui) return 'en-cours'
  if (a.echeanceReportee && a.echeanceReportee >= aujourdhui) return 'reportee'
  return 'en-retard'
}

export function estTerminee(statut: StatutAction): boolean {
  return statut === 'terminee-a-temps' || statut === 'terminee-en-retard'
}

/** Échéance qui compte aujourd'hui : la reportée si elle existe. */
export function echeanceEnVigueur(a: Pick<ActionFnct, 'echeance' | 'echeanceReportee'>): Jour | null {
  return a.echeanceReportee ?? a.echeance
}

export interface CompteursActions {
  total: number
  aPlanifier: number
  enCours: number
  reportees: number
  enRetard: number
  terminees: number
}

export function compterActions(actions: readonly ActionFnct[], aujourdhui: Jour): CompteursActions {
  const c: CompteursActions = { total: 0, aPlanifier: 0, enCours: 0, reportees: 0, enRetard: 0, terminees: 0 }
  for (const a of actions) {
    c.total += 1
    const s = statutAction(a, aujourdhui)
    if (s === 'a-planifier') c.aPlanifier += 1
    else if (s === 'en-cours') c.enCours += 1
    else if (s === 'reportee') c.reportees += 1
    else if (s === 'en-retard') c.enRetard += 1
    else c.terminees += 1
  }
  return c
}

// ─── Risque ───

/**
 * RPN = Severity × Frequency × Detectability. Comme la base : sans
 * détectabilité, on compte 5. Sans sévérité ou fréquence : pas de RPN.
 */
export function rpn(f: Pick<Fnct, 'severite' | 'frequence' | 'detectabilite'>): number | null {
  if (f.severite === null || f.frequence === null) return null
  return f.severite * f.frequence * (f.detectabilite ?? 5)
}

/** Critical si sévérité 10 ou RPN ≥ 490 ; Minor jusqu'à 70 ; Major entre les deux. */
export function criticiteRpn(f: Pick<Fnct, 'severite' | 'frequence' | 'detectabilite'>): CriticiteRpn | null {
  const r = rpn(f)
  if (r === null) return null
  if (f.severite === 10 || r >= 490) return 'Critical'
  if (r <= 70) return 'Minor'
  return 'Major'
}

// ─── État d'une FNC-T ───

export function estClose(f: Pick<Fnct, 'cloture'>): boolean {
  return f.cloture !== null
}

/**
 * En relevé d'écarts P&ID / PPL (14/10, docs/chantier-fnct.md § 13) : le N° est
 * pris au premier écart, l'ouverture viendra plus tard. Il faut des écarts : les
 * FNC-T déjà dans SCE_FNCT avant le 14/10 (essai d'import, escalades) ont une
 * ouverture vide jusqu'au prochain import, sans être des relevés.
 */
export function estEnReleve(f: Pick<Fnct, 'ouverture' | 'ecarts' | 'cloture'>): boolean {
  return !f.ouverture && (f.ecarts ?? '').trim() !== '' && f.cloture === null
}

/** D'où se comptent l'ancienneté et les retards : l'ouverture, sinon l'enregistrement. */
export function debutFnct(f: Pick<Fnct, 'ouverture' | 'enregistrement'>): Jour | null {
  return f.ouverture || f.enregistrement
}

/** Jours écoulés entre deux dates `AAAA-MM-JJ` (négatif si `fin` précède `debut`). */
export function joursEntre(debut: Jour, fin: Jour): number {
  return Math.round((Date.parse(`${fin}T00:00:00Z`) - Date.parse(`${debut}T00:00:00Z`)) / 86_400_000)
}

// ─── Jours ouvrés (délais par défaut des actions, 14/10) ───

/** Dimanche de Pâques d'une année (calcul grégorien de Meeus / Butcher). */
function paques(annee: number): Date {
  const a = annee % 19
  const b = Math.floor(annee / 100)
  const c = annee % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mois = Math.floor((h + l - 7 * m + 114) / 31)
  const jour = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(Date.UTC(annee, mois - 1, jour, 12))
}

const decaler = (d: Date, jours: number): Jour => {
  const x = new Date(d)
  x.setUTCDate(x.getUTCDate() + jours)
  return x.toISOString().slice(0, 10)
}

/** Les jours fériés de France métropolitaine (Pompey : pas de régime Alsace-Moselle). */
export function joursFeries(annee: number): Set<Jour> {
  const p = paques(annee)
  const fixes = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'].map((md) => `${annee}-${md}`)
  return new Set([...fixes, decaler(p, 1), decaler(p, 39), decaler(p, 50)]) // lundi de Pâques, Ascension, lundi de Pentecôte
}

/** Jour ouvré : du lundi au vendredi, hors jour férié. */
export function estOuvre(j: Jour): boolean {
  const jourSemaine = new Date(`${j}T12:00:00Z`).getUTCDay()
  return jourSemaine !== 0 && jourSemaine !== 6 && !joursFeries(Number(j.slice(0, 4))).has(j)
}

/** `2026-10-14` + 10 jours ouvrés → `2026-10-28` (week-ends et jours fériés sautés). */
export function ajouterJoursOuvres(j: Jour, n: number): Jour {
  let d = j
  let restants = n
  while (restants > 0) {
    d = decaler(new Date(`${d}T12:00:00Z`), 1)
    if (estOuvre(d)) restants -= 1
  }
  return d
}

/** `2026-01-27` → `27/01/2026` (interface) ; tiret long si absent. */
export function jourLisible(j: Jour | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(j ?? '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—'
}

/** Aujourd'hui, en heure de Paris : `AAAA-MM-JJ`. */
export function aujourdhuiParis(maintenant: Date = new Date()): Jour {
  return jourParis(maintenant)
}

/** Une date/heure → le jour qu'elle désigne à Pompey. */
export function jourParis(d: Date): Jour {
  // en-CA écrit AAAA-MM-JJ.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

// ─── Machines ───

/**
 * N° de série d'une cellule de la base : « 4023-H + 4024-H + 4025-H »,
 * « 4655-H/4656-H », « 4220-SC & 4681-SC », « 4464-H 4465-H », retours à la
 * ligne compris. « 4260S » est remis en « 4260-S » ; « 1245-H-06 » garde son
 * indice. En MAJUSCULES, sans doublon, dans l'ordre de la cellule.
 */
export function decouperNumerosSerie(texte: string | null | undefined): string[] {
  const vus = new Set<string>()
  const res: string[] = []
  for (const brut of (texte ?? '').split(/[+/&,;\s]+/)) {
    const t = brut.trim().toUpperCase()
    if (!t) continue
    const m = /^(\d{4})-?([A-Z]{1,2})(-\d+)?$/.exec(t)
    const ns = m ? `${m[1]}-${m[2]}${m[3] ?? ''}` : t
    if (!vus.has(ns)) {
      vus.add(ns)
      res.push(ns)
    }
  }
  return res
}

// ─── Personnes ───

/** Clé de comparaison d'un nom : sans accents, sans casse, espaces réduits. */
export function cleNom(nom: string | null | undefined): string {
  return (nom ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s.,'’-]+/g, ' ')
    .trim()
}

/**
 * Même personne, quel que soit l'ordre (« Vautelin Sébastien » / « Sébastien
 * Vautelin »), la casse ou les accents : mêmes mots, dans un ordre ou l'autre.
 */
export function memePersonne(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = cleNom(a).split(' ').filter(Boolean).sort().join(' ')
  const y = cleNom(b).split(' ').filter(Boolean).sort().join(' ')
  return x !== '' && x === y
}
